/**
 * @fileoverview Vuex mutations
 * @module store/mutations
 * @see module:store/mutations
 */

import { getStorageAuthInfo, storage, cookie } from '@/utils/storageOperateApi';
import { isEmptyObject, encrypt } from '@/utils/api';
import { LOCAL_STORAGE_KEY, DECRYPT_KEY } from '@/constants';
import { getSearchHint, setSearchHint } from '@/utils/globalSearchMind';
import { getHiddenFeatByOrg } from '@/utils/getPermissibleMenu';
import { stateTemplate } from './state';

export default {
  // 设置当前登录态
  setIsLogin(state, payload) {
    state.isLogin = payload;
  },
  // 设置当前录音状态
  setIsRecording(state, payload) {
    state.isRecording = payload;
  },
  changeTaskCenterActiveName(state, v) {
    state.taskCenterActiveName = v;
  },
  changeUploadList(state, v) {
    state.uploadList = v;
  },
  // 添加uploadList
  unshiftUploadList(state, v) {
    if (Array.isArray(v)) {
      v.forEach((item) => {
        state.uploadList.unshift(item);
      });
    } else {
      state.uploadList.unshift(v);
    }
  },
  // 通过索引删除uploadList
  removeUploadListByIndex(state, index) {
    state.uploadList.splice(index, 1);
  },
  changeDownloadList(state, v) {
    state.downloadList = v;
  },
  // 添加downloadList
  unshiftDownloadList(state, v) {
    if (Array.isArray(v)) {
      v.forEach((item) => {
        state.downloadList.unshift(item);
      });
    } else {
      state.downloadList.unshift(v);
    }
  },
  // 通过索引删除downloadList
  removeDownloadListByIndex(state, index) {
    state.downloadList.splice(index, 1);
  },
  changeTaskPanel(state, v) {
    state.taskPanel = v;
  },
  changeMenuCollapse(state, v) {
    state.menuIsCollapse = v;
  },
  showTooltips(state, text) {
    state.tooltips = true;
    state.ttText = text;
  },
  hideTooltips(state) {
    state.tooltips = false;
  },
  // 初始化state
  initState(state, { authInfo, goId }) {
    state.authUserInfo = authInfo;
    state.userInfo = authInfo?.userinfo;
    if (!state.userInfo) {
      state.userInfo = {
        comeinUid: authInfo?.brmToken?.comeinUid,
        initialPassword: authInfo?.brmToken?.initialPassword,
        roleId: authInfo?.brmToken?.roleId,
        id: authInfo?.brmToken?.userId
      };
    }
    state.goId = goId;
    state.hiddenFeartByOrg = getHiddenFeatByOrg(goId);
    state.globalSearchHint = getSearchHint();
    state.globalSearchIdMap = storage.get(LOCAL_STORAGE_KEY.GLOBAL_SEARCH_ID_MAP) || {};
  },
  /** 根据组织 ID 设置是否隐藏功能 */
  setHiddenFeartByOrg(state, payload) {
    state.hiddenFeartByOrg = payload;
  },
  // 重置state
  clearState(state) {
    Object.assign(state, JSON.parse(JSON.stringify(stateTemplate)));
  },

  // 设置用户信息
  setUserInfo(state, userInfo) {
    state.userInfo = userInfo;
  },

  // 设置下载进度条
  setDownloadProgress(state, s) {
    state.downloadProgress = s;
  },

  // 导航收缩传递监听事件
  onEvent(state, { event, fn }) {
    if (!state.eventList) {
      state.eventList = [];
    }

    const index = state.eventList.findIndex((item) => item.event === event);
    index >= 0 && state.eventList.splice(index, 1);

    state.eventList.push({ event, fn });
  },
  offEvent(state, { event }) {
    if (state.eventList) {
      const index = state.eventList.findIndex((item) => item.event === event);
      index >= 0 && state.eventList.splice(index, 1);
    }
  },

  /**
   * 设置用户鉴权信息
   */
  setAuthUserInfo(state, userInfo) {
    state.authUserInfo = userInfo;
  },
  /**
   * 设置用户开通会员信息
   */
  setUserMembership(state, payload) {
    state.userMembership = payload;
  },

  /**
   * 设置投研面包屑
   * @param {*} state
   * @param {* Object } value
   */
  setBreadCurmb(state, value) {
    state.breadcrumb = value;
  },
  // 设置地区/国家区号
  setAreaCodeOptions(state, payload) {
    state.areaCodeOptions = payload;
  },
  setContainerSize(state, payload) {
    state.containerSize = payload;
  },
  // 同步设置用户信息
  syncUserInfo(state, payload) {
    // 获取本地存储的用户信息
    const oldAuthInfo = state.authUserInfo;
    const authUserInfo = oldAuthInfo && !isEmptyObject(oldAuthInfo) ? state.authUserInfo : getStorageAuthInfo();
    authUserInfo.userinfo = authUserInfo.userinfo || {};
    // 合并用户信息
    Object.assign(authUserInfo.userinfo, payload);
    setTimeout(() => {
      // 本地缓存用户信息
      storage.set(LOCAL_STORAGE_KEY.BRM_USERINFO, authUserInfo, {}, true);
      // 同步用户信息到vuex
      this.commit('setUserInfo', payload);
    }, 500);
  },
  // 设置机构logo
  setOrgLogo(state, payload) {
    state.orgLogo = payload;
  },
  setOrgMiniLogo(state, payload) {
    state.orgMiniLogo = payload;
  },
  setGoId(state, payload) {
    state.goId = payload;
  },
  // 设置全局搜索联想词相关信息
  setGlobalSearchHint(state, payload) {
    state.globalSearchHint = payload;
    setSearchHint(payload);
  },
  // 设置全局搜索唯一标识
  setGlobalSearchIdMap(state, payload) {
    const { sourcePageKey, searchId } = payload;
    state.globalSearchIdMap[sourcePageKey] = searchId;
    storage.set(LOCAL_STORAGE_KEY.GLOBAL_SEARCH_ID_MAP, state.globalSearchIdMap);
  },
  // 设置机构信息
  setOrgInfo(state, payload) {
    state.orgInfo = payload;
  },
  // 设置用户头像
  setUserAvatar(state, payload) {
    state.userAvatar = payload;
  },
  // 设置页面缓存
  pushKeepAlive(state, payload) {
    if (state.keepAlive.includes(payload)) return;
    state.keepAlive.push(payload);
  },
  // 移除页面缓存
  removeKeepAlive(state, payload) {
    const index = state.keepAlive.findIndex((item) => item === payload);
    if (index >= 0) {
      state.keepAlive.splice(index, 1);
    }
  },
  // 清空页面缓存
  clearKeepAlive(state) {
    state.keepAlive = [];
  },
  // 设置页面缓存是否需要刷新
  changeKeepAliveNeedRefresh(state, payload) {
    state.keepAliveNeedRefresh = payload;
  },
  // 打开分析师抽屉
  showAnalystDrawer(state, payload) {
    state.analystVisible = true;
    state.analystUid = payload.analystUid;
    state.analystInfo = payload;
  },
  // 关闭分析师抽屉
  hideAnalystDrawer(state) {
    state.analystVisible = false;
  },
  // 设置合作方信息
  setBridgeInfo(state, payload) {
    state.bridgeInfo = payload;
  },

  // 数据同步
  setDataSync(state, data) {
    state.dataSync = data;
  },

  setQueryingSync(state, data) {
    state.queryingSync = data;
  },

  // 设置electron窗口tab集合
  setElectronTabs(state, payload) {
    state.electronTabs = payload;
  },

  setShowLive(state, payload) {
    state.showLive = payload;
  },

  setSecurityAuth(state, obj) {
    state.securityAuth = obj.securityAuth;
    obj.securityAuthType && (state.securityAuthType = obj.securityAuthType);
    obj.securityAuthContent && (state.securityAuthContent = obj.securityAuthContent);
    obj.securityAuthCloseBtn && (state.securityAuthCloseBtn = obj.securityAuthCloseBtn);
    obj.securityAuthSuccessBtn && (state.securityAuthSuccessBtn = obj.securityAuthSuccessBtn);
  },

  setSecurityFace(state, obj) {
    state.securityFace = obj.securityFace;
    obj.securityFaceType && (state.securityFaceType = obj.securityFaceType);
    obj.securityFaceCloseBtn && (state.securityFaceCloseBtn = obj.securityFaceCloseBtn);
    obj.securityFaceSuccessBtn && (state.securityFaceSuccessBtn = obj.securityFaceSuccessBtn);
  },

  setAiChatSetting(state, data) {
    if (!data.id) return;
    state.aiChatSetting[data.id] = Object.assign({}, state.aiChatSetting[data.id], data.obj);
  },
  setDialogIframe(state, data) {
    const obj = {};
    obj.visible = true;
    obj.showQrCode = data.showQrCode || false;

    obj.url =
      data.detailUrl +
      `&isFrame=1&userInfo=${encodeURIComponent(encrypt(JSON.stringify(state.userInfo), DECRYPT_KEY))}`;
    obj.urlCode = data.detailUrl;
    state.dialogIframe = obj;
  },
  // 设置支付弹窗
  setWebPayDialog(state, data) {
    // 添加时间戳作为唯一标识符
    if (data.visible) {
      data.timestamp = new Date().getTime();
    }
    state.webPayDialog = data;
  },
  // 设置牛币充值弹窗（外部：commit('setNbiRechargeDialog', { visible: true, onSuccess?: fn })）
  setNbiRechargeDialog(state, data) {
    const prev = state.nbiRechargeDialog || {};
    const next = { ...prev, ...data };
    if (data.visible === true) {
      next.timestamp = Date.now();
      next.pendingPaySuccess = false;
      next.onSuccess = data.onSuccess;
    }
    if (data.visible === false && data.pendingPaySuccess !== true) {
      next.onSuccess = null;
    }
    state.nbiRechargeDialog = next;
  },
  /**
   * 设置牛币明细弹窗显隐（外部：commit('setNbiDetailDialog', { visible: true })）
   * @param {Object} state - Vuex state
   * @param {{ visible: boolean }} data - 目标显隐状态
   */
  setNbiDetailDialog(state, data) {
    state.nbiDetailDialog = { ...state.nbiDetailDialog, ...data };
  },
  // 设置「会员/牛币付费弹窗代理给宿主」开关（外部：commit('setMemberDelegate', { enabled: true, hostOrigin }))
  setMemberDelegate(state, data) {
    state.memberDelegate = { ...state.memberDelegate, ...data };
  },
  setAiAvatarListFilter(state, data) {
    state.aiAvatarListFilter = data;
  },
  clearAiAvatarListFilter(state) {
    state.aiAvatarListFilter = null;
  },

  setLang(state, lang) {
    state.lang = lang;
    cookie.set(LOCAL_STORAGE_KEY.LANGUAGE, lang, {
      domain: process.env.NODE_ENV === 'development' ? '' : '.comein.cn'
    });
    cookie.set(LOCAL_STORAGE_KEY.LANGUAGE, lang, {
      domain: process.env.NODE_ENV === 'development' ? '' : '.finenter.ai'
    });
    localStorage.setItem('lang', lang);
  },

  // 多文件上传管理
  /**
   * 添加或更新上传任务
   * @param {String} payload.fileId - 文件唯一标识
   * @param {Object} payload.ossClient - OSS 客户端实例
   * @param {Object} payload.checkpoint - 上传检查点
   * @param {String} payload.fileName - 文件名
   * @param {Number} payload.fileSize - 文件大小
   */
  setUploadTask(state, { fileId, ossClient, checkpoint, fileName, fileSize }) {
    if (state.uploadTasks[fileId]) return;
    state.uploadTasks[fileId] = {
      ...state.uploadTasks[fileId],
      ossClient,
      checkpoint,
      fileName,
      fileSize
    };
  },

  /**
   * 删除上传任务
   * @param {String} fileId - 文件唯一标识
   */
  removeUploadTask(state, fileId) {
    delete state.uploadTasks[fileId];
  },

  /**
   * 清空所有上传任务
   */
  removeAllUploadTasks(state) {
    state.uploadTasks = {};
  },

  /**
   * 设置AI聊天首页发送参数
   * @param {Object} state
   * @param {Object} payload - 发送参数 { keyword, fileList, agentType, agentParams, mcpServerList, aichatId, mode }
   */
  setChatHomeSendParams(state, payload) {
    state.chatHomeSendParams = payload;
  },

  /**
   * 清空AI聊天首页发送参数
   * @param {Object} state
   */
  clearChatHomeSendParams(state) {
    state.chatHomeSendParams = null;
  },
  /**
   * 设置海外用户订阅套餐
   * @param {Object} state
   * @param {String} payload - 计划名称
   */
  setOverseasUserPlan(state, payload) {
    state.overseasUserPlan = payload;
  },
  /**
   * 设置海外用户订阅完整数据
   * @param {Object} state
   * @param {Object} payload - 订阅数据
   */
  setOverseasMemberData(state, payload) {
    state.overseasMemberData = payload;
  },

  setIrmCompanyInfo(state, payload) {
    state.irmCompanyInfo = { ...state.irmCompanyInfo, ...payload };
  }
};
